import { useMemo, useState } from 'react';
import { useCallable, CallStatus } from '../lib/useCallable';

/**
 * Where a completed candidate form goes, which mailbox sends it, and what domain a candidate sees.
 *
 * ON THIS PAGE rather than in Admin, and directly beneath the warning it resolves. The person who
 * reads "a completed form has nowhere to go" is the person who should be able to fix it — and
 * until now this page told them to go and hand-edit a Firestore document, which is not a thing an
 * app should ask of anybody. Every other rule in this system is editable from a screen; this one
 * was an oversight, not a design.
 *
 * `adminUpdateSettings` REPLACES the document, so every field is submitted together whether it
 * was touched or not. Sending only the changed one would silently delete the rest.
 */

const input =
  'rounded border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-600 dark:bg-slate-900';

/** A stored list, as one line somebody can edit. */
export const asText = (value) => (Array.isArray(value) ? value.join(', ') : String(value || ''));

export default function CandidateFormSettings({ settings, onDefaultDomain, docId = 'candidateForm', noun = 'candidate' }) {
  const { run, busy, error, saved } = useCallable();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const stored = useMemo(
    () => ({
      hrRecipients: asText(settings && settings.hrRecipients),
      hrCc: asText(settings && settings.hrCc),
      inviteSubject: String((settings && settings.inviteSubject) || ''),
      senderName: String((settings && settings.senderName) || ''),
      senderUpn: String((settings && settings.senderUpn) || ''),
      reviewLinkLabel: String((settings && settings.reviewLinkLabel) || ''),
      publicBaseUrl: String((settings && settings.publicBaseUrl) || ''),
      inviteIntro: String((settings && settings.inviteIntro) || ''),
    }),
    [settings]
  );

  const value = draft || stored;
  const set = (key, next) => setDraft({ ...value, [key]: next });
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(stored);

  const save = async () => {
    const result = await run('adminUpdateSettings', { docId, value });
    // Only clear the draft on success, so a rejected value stays on screen to be corrected
    // rather than silently reverting to what is stored.
    if (result !== null) setDraft(null);
  };

  const field = (key, label, placeholder, hint) => (
    <label className="block text-xs text-slate-600 dark:text-slate-300">
      {label}
      <input
        className={`${input} mt-1 w-full`}
        value={value[key]}
        placeholder={placeholder}
        onChange={(event) => set(key, event.target.value)}
      />
      {hint ? <span className="mt-1 block text-slate-500 dark:text-slate-400">{hint}</span> : null}
    </label>
  );

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">Settings</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open ? (
        <div className="mt-3 space-y-3">
          {field(
            'hrRecipients',
            'Send completed forms to',
            'hr@aalrr.com',
            'Separate several addresses with commas. Nothing is emailed until this is set.'
          )}
          {field('hrCc', 'Copy to', 'hiring.partner@aalrr.com', 'Optional — the hiring partner, for instance.')}
          {field(
            'inviteSubject',
            'Invitation subject line',
            'Conflict check and professional disclosure form — {position}',
            `The first thing a ${noun} reads. Write {name} or {position} to have them filled in. Leave empty for the default.`
          )}
          {field(
            'reviewLinkLabel',
            'Link button in the completed-form email',
            'Open the submitted form',
            'The button HR sees. It opens that form on this page — signing in is still required. Leave empty for “Open the submitted form”.'
          )}
          {field(
            'senderName',
            'From name',
            'Candidate Conflicts',
            `What the recipient reads in their From column. Left empty, it is the form's own default — without it they would see the name of whichever mailbox sends, which means nothing to a ${noun}.`
          )}
          {field(
            'senderUpn',
            'Send as',
            'recruiting@aalrr.com',
            'Optional. The mailbox both emails appear to come from. The Graph app registration must be allowed to send as it, or the send fails and shows in Admin → Mail Queue.'
          )}
          {field(
            'publicBaseUrl',
            `Domain ${noun}s see`,
            onDefaultDomain ? 'https://labs.aalrr.com' : '',
            onDefaultDomain
              ? 'Optional. Links point at aalrrnewclientintake.web.app today. Add a domain in Firebase Hosting, add it to Authentication → Authorized domains, then set it here. Nothing else changes and the web.app address keeps working.'
              : 'Links are issued on this domain.'
          )}
          <label className="block text-xs text-slate-600 dark:text-slate-300">
            Invitation wording
            <textarea
              className={`${input} mt-1 w-full`}
              rows={3}
              placeholder="Leave empty for the default wording."
              value={value.inviteIntro}
              onChange={(event) => set('inviteIntro', event.target.value)}
            />
            <span className="mt-1 block text-slate-500 dark:text-slate-400">
              Replaces the first paragraph of the invitation email. Leave empty for the default.
            </span>
          </label>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={busy || !dirty}
              className="rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 disabled:opacity-50 dark:bg-firm-500 dark:hover:bg-firm-400"
            >
              Save settings
            </button>
            {dirty ? (
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="text-xs text-slate-500 underline dark:text-slate-400"
              >
                Discard
              </button>
            ) : null}
            <CallStatus busy={busy} error={error} saved={saved} />
          </div>
        </div>
      ) : null}
    </section>
  );
}
